import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { usePaymentHistoryForUser } from "@/hooks/useAccounting";
import { BADGE_TONES, pickAssortedBadgeClass } from "@/lib/badge-tones";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getPaymentStatusBadgeClass } from "@/lib/status-tag-colors";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function PaymentHistoryTab({ userId }: { userId: string }) {
  const history = usePaymentHistoryForUser(userId);

  if (history === undefined) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>
              Payment History
              <Skeleton className="ml-2 inline-block h-4 w-20 align-middle" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {history ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Payment History
              {history.total !== undefined ? (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({history.total} records)
                </span>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.payments && history.payments.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="text-muted-foreground hover:bg-transparent">
                    <TableHead className="font-medium">Date</TableHead>
                    <TableHead className="font-medium">Order</TableHead>
                    <TableHead className="font-medium">Enterprise</TableHead>
                    <TableHead className="font-medium">Invoice</TableHead>
                    <TableHead className="font-medium text-right">Amount</TableHead>
                    <TableHead className="font-medium">Status</TableHead>
                    <TableHead className="font-medium">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.payments.map((p: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.createdAt ? formatDate(p.createdAt) : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {p.orderNumber || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getEnterpriseBadgeClass(
                            p.isEnterprise,
                          )}
                        >
                          {getEnterpriseBadgeLabel(
                            p.isEnterprise,
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {p.orderId ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              window.open(
                                `/invoice/${p.orderId}`,
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                          >
                            Invoice
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(p.amount ?? 0)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            getPaymentStatusBadgeClass(p.status),
                            "text-xs",
                          )}
                        >
                          {formatStatusLabel(p.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {p.paymentType ? (
                          <Badge
                            variant="outline"
                            className={pickAssortedBadgeClass(p.paymentType)}
                          >
                            {formatStatusLabel(p.paymentType)}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className={BADGE_TONES.slate}>
                            N/A
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No payment records found
              </p>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
